/**
 * Spec 084 — typed API client for the Rule Library.
 *
 * Public read endpoints (protected by Orbit auth only, no role check):
 *   GET /api/v1/rules?status=...&epistemic_status=...&applicability_engines=...
 *       &applicability_content_types=...&is_current_version=true
 *   GET /api/v1/rules/active
 *   GET /api/v1/rules/:id                     → includes provenance
 *   GET /api/v1/rules/by-slug/:slug           → current version
 *   GET /api/v1/rules/by-slug/:slug/history   → all versions, DESC by version
 *
 * Admin write endpoints (require `rule_admin` or `orbit:admin`):
 *   POST /api/v1/admin/rules/:id/activate
 *   POST /api/v1/admin/rules/:id/deprecate    body { reason: string }
 *
 * Types intentionally duplicated from `src/rules/types.ts` — the Vite client
 * and the Fastify server share no module graph.
 */
import { apiFetch, apiGet } from '../lib/api.js';

export type RuleStatus = 'approved_inactive' | 'active' | 'deprecated';

export type EpistemicStatus =
  | 'community_heuristic_single_source'
  | 'community_consensus'
  | 'research_backed'
  | 'internally_validated';

export type ScoringMode = 'g_eval' | 'dag';

export type Engine = 'chatgpt' | 'gemini' | 'claude' | 'google_aio' | 'mistral';

export type SourceType =
  | 'medium_post'
  | 'practitioner_blog'
  | 'research_paper'
  | 'internal'
  | 'client_feedback';

export interface Rule {
  id: string;
  slug: string;
  version: number;
  is_current_version: boolean;
  name: string;
  definition: string;
  evaluation_steps: Record<string, unknown>;
  scoring_mode: ScoringMode;
  dag_structure: Record<string, unknown> | null;
  status: RuleStatus;
  epistemic_status: EpistemicStatus;
  applicability_engines: Engine[];
  applicability_content_types: string[];
  weight: number;
  embedding_model: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  activated_at: string | null;
  deprecated_at: string | null;
  deprecation_reason: string | null;
  provenance?: RuleProvenance[];
}

export interface RuleProvenance {
  id: string;
  rule_id: string;
  source_type: SourceType;
  source_url: string;
  source_author: string;
  source_title: string | null;
  source_date: string | null;
  supporting_quote: string | null;
  added_at: string;
  added_by: string;
}

/* ─── Reads ────────────────────────────────────────────────────── */

export interface ListRulesFilters {
  status?: RuleStatus;
  epistemic_status?: EpistemicStatus;
  applicability_engines?: Engine[];
  applicability_content_types?: string[];
  scoring_mode?: ScoringMode;
  is_current_version?: boolean;
}

function normalizeRule(raw: Rule): Rule {
  return {
    ...raw,
    provenance: raw.provenance ?? undefined,
  };
}

export async function listRules(
  filters: ListRulesFilters = {},
): Promise<Rule[]> {
  const q = new URLSearchParams();
  if (filters.status) q.set('status', filters.status);
  if (filters.epistemic_status) q.set('epistemic_status', filters.epistemic_status);
  if (filters.applicability_engines && filters.applicability_engines.length > 0) {
    for (const e of filters.applicability_engines) q.append('applicability_engines', e);
  }
  if (
    filters.applicability_content_types &&
    filters.applicability_content_types.length > 0
  ) {
    for (const c of filters.applicability_content_types)
      q.append('applicability_content_types', c);
  }
  if (filters.is_current_version !== undefined) {
    q.set('is_current_version', String(filters.is_current_version));
  }
  const url = `/api/v1/rules${q.size ? `?${q}` : ''}`;
  const res = await apiGet<{ data: Rule[] }>(url);
  const rows = res.data.map(normalizeRule);
  // Server does not filter by scoring_mode — apply client-side for v1.
  return filters.scoring_mode
    ? rows.filter((r) => r.scoring_mode === filters.scoring_mode)
    : rows;
}

export async function getRule(id: string): Promise<Rule> {
  const res = await apiGet<{ data: Rule }>(`/api/v1/rules/${id}`);
  return normalizeRule(res.data);
}

export async function getRuleHistory(slug: string): Promise<Rule[]> {
  const res = await apiGet<{ data: Rule[] }>(
    `/api/v1/rules/by-slug/${encodeURIComponent(slug)}/history`,
  );
  return res.data.map(normalizeRule);
}

/* ─── Writes (admin-only) ──────────────────────────────────────── */

export async function activateRule(id: string): Promise<Rule> {
  const res = await apiFetch(`/api/v1/admin/rules/${id}/activate`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Activate failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: Rule };
  return normalizeRule(json.data);
}

export async function deprecateRule(id: string, reason: string): Promise<Rule> {
  const res = await apiFetch(`/api/v1/admin/rules/${id}/deprecate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Deprecate failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: Rule };
  return normalizeRule(json.data);
}

export interface ProvenanceValidation {
  provenance_id: string;
  source_url: string;
  status: 'ok' | 'broken' | 'skipped';
  http_status: number | null;
  detail: string | null;
}

export async function validateRuleProvenance(id: string): Promise<ProvenanceValidation[]> {
  const res = await apiFetch(`/api/v1/admin/rules/${id}/provenance/validate`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Validate failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: ProvenanceValidation[] };
  return json.data;
}
